import { useEffect, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Quantum Key (level 90). Two steps:
//   1. Pick a basis (+ or x) for every bit and measure ONCE. The right basis reads the bit,
//      the wrong one returns noise and destroys it.
//   2. The true bases are then announced, so you know which readings to trust; rebuild the
//      destroyed bits from the parity syndromes and submit the key.
//
// The key never reaches the browser — the server grades the reconstruction. The syndrome
// counter below is a local aid computed from data the server already sent.

const RECT = '+';
const DIAG = 'x';

interface Challenge {
  bits: number;
  syndromes: number[];
  hasSyndromes: boolean;
  /** index → true basis, granted by a high PasswordHashing. */
  preRevealedBases: Record<string, string>;
  timeLimitSec: number;
  /**
   * Only on a RESUMED attempt whose channel was already read. The channel can be measured
   * once, so the reconstruction is picked back up from here instead of offering a measurement
   * the server would refuse.
   */
  measured?: boolean;
  measurement?: MeasureResult | null;
}

interface MeasureResult {
  bits: number;
  readings: number[];
  correct: boolean[];
  trueBases: string[];
  syndromes: number[];
  /** index → bit, released so the reconstruction always has a unique answer. */
  revealed: Record<string, number>;
  correctCount: number;
}

/** Seed the reconstruction with everything already certain (clean readings + recovery bits). */
function seedFrom(res: MeasureResult): number[] {
  return Array.from({ length: res.bits }, (_, i) => {
    if (res.correct[i]) return res.readings[i];
    const rev = res.revealed[String(i)];
    return rev === undefined ? 0 : rev;
  });
}

export default function QuantumKeyView({ challenge, onAct, onSubmit, setPendingSubmission, busy }: MinigameViewProps<Challenge, MeasureResult>) {
  const bits = challenge?.bits ?? 9;
  const preRevealed = challenge?.preRevealedBases ?? {};

  const [chosen, setChosen] = useState<string[]>([]);
  const [measured, setMeasured] = useState<MeasureResult | null>(null);
  const [draft, setDraft] = useState<number[]>([]);

  // Fresh attempt: start from the pre-revealed bases where we have them. A resumed attempt
  // that had already measured is restored to its reconstruction, seeded exactly as the
  // measurement seeded it.
  useEffect(() => {
    const done = challenge?.measurement ?? null;
    setChosen(Array.from({ length: bits }, (_, i) => done?.trueBases?.[i] ?? preRevealed[String(i)] ?? RECT));
    setMeasured(done);
    if (done) {
      const seeded = seedFrom(done);
      setDraft(seeded);
      setPendingSubmission({ key: seeded });
    } else {
      setDraft(Array(bits).fill(0));
      setPendingSubmission({ key: [] });
    }
    // preRevealed comes from `challenge`; listing it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge, bits]);

  const flip = (i: number) => {
    if (busy || measured) return;
    if (preRevealed[String(i)]) return;               // already known — no reason to change it
    setChosen(c => c.map((b, k) => (k === i ? (b === RECT ? DIAG : RECT) : b)));
  };

  const measure = async () => {
    if (busy || measured) return;
    const res = await onAct({ bases: chosen });
    if (!res) return;                                  // rejected, or the attempt ended
    setMeasured(res);
    const seeded = seedFrom(res);
    setDraft(seeded);
    setPendingSubmission({ key: seeded });
  };

  const setBit = (i: number, value: number) => {
    if (busy || !measured) return;
    if (measured.correct[i]) return;                   // certain — not editable
    if (measured.revealed[String(i)] !== undefined) return;
    const next = draft.map((b, k) => (k === i ? value : b));
    setDraft(next);
    setPendingSubmission({ key: next });
  };

  // How many syndromes the current reconstruction satisfies — the whole point of the puzzle.
  const syndromes = measured?.syndromes ?? [];
  const satisfied = syndromes.filter((p, i) => ((draft[i] ?? 0) ^ (draft[i + 1] ?? 0) ^ (draft[i + 2] ?? 0)) === p).length;
  const allSatisfied = syndromes.length > 0 && satisfied === syndromes.length;

  const cell = (extra: React.CSSProperties = {}): React.CSSProperties => ({
    width: '2.1rem', height: '2.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 4, fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 'bold',
    border: '1px solid #2a2a2a', background: '#0f1218', color: '#e2e8f0', ...extra,
  });

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {!measured ? (
        <>
          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.7rem', lineHeight: 1.5 }}>
            Each of the <strong>{bits}</strong> bits is encoded in a basis: <strong>+</strong> or{' '}
            <strong>x</strong>. Pick one per bit — the right basis reads the bit, the{' '}
            <span style={{ color: '#f87171' }}>wrong one destroys it</span>. You measure once.
            {Object.keys(preRevealed).length > 0 && (
              <> Your PasswordHashing already exposed{' '}
                <span style={{ color: '#4ade80' }}>{Object.keys(preRevealed).length}</span> of them.</>
            )}
          </p>

          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
            {Array.from({ length: bits }, (_, i) => {
              const known = preRevealed[String(i)] !== undefined;
              return (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ color: '#4b5563', fontSize: '0.6rem', marginBottom: 2 }}>{i}</div>
                  <button
                    onClick={() => flip(i)}
                    disabled={busy || known}
                    title={known ? 'Basis already known' : 'Click to switch basis'}
                    style={cell({
                      border: `1px solid ${known ? '#4ade80' : 'var(--primary-color)'}`,
                      color: known ? '#86efac' : 'var(--primary-color)',
                      background: known ? '#052e16' : 'transparent',
                      cursor: busy || known ? 'not-allowed' : 'pointer',
                      padding: 0,
                    })}
                  >
                    {chosen[i] ?? RECT}
                  </button>
                </div>
              );
            })}
          </div>

          <button className="primary" onClick={measure} disabled={busy} style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}>
            {busy ? '…' : 'Measure the channel'}
          </button>
          {!challenge?.hasSyndromes && (
            <p style={{ color: '#fbbf24', fontSize: '0.75rem', marginTop: '0.5rem' }}>
              Warning: without PasswordHashing v12 you get no parity syndromes — destroyed bits
              cannot be recovered.
            </p>
          )}
        </>
      ) : (
        <>
          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.7rem', lineHeight: 1.5 }}>
            Bases announced: <strong style={{ color: '#4ade80' }}>{measured.correctCount}</strong> of{' '}
            {measured.bits} readings are trustworthy. Rebuild the rest so that{' '}
            <strong>every parity</strong> checks out — each syndrome is the XOR of three
            consecutive bits.
            {Object.keys(measured.revealed).length > 0 && (
              <> <span style={{ color: '#60a5fa' }}>{Object.keys(measured.revealed).length} recovery bit(s)</span> were
                released so the answer is unique.</>
            )}
          </p>

          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
            {Array.from({ length: measured.bits }, (_, i) => {
              const certain = measured.correct[i];
              const rev = measured.revealed[String(i)] !== undefined;
              const locked = certain || rev;
              const value = draft[i] ?? 0;
              return (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ color: '#4b5563', fontSize: '0.6rem', marginBottom: 2 }}>{i}</div>
                  <button
                    onClick={() => setBit(i, value === 0 ? 1 : 0)}
                    disabled={busy || locked}
                    title={certain ? `read cleanly in basis ${measured.trueBases[i]}`
                      : rev ? 'recovery bit' : 'destroyed — deduce it'}
                    style={cell({
                      border: `1px solid ${certain ? '#4ade80' : rev ? '#60a5fa' : '#fbbf24'}`,
                      color: certain ? '#86efac' : rev ? '#93c5fd' : '#fde047',
                      background: certain ? '#052e16' : rev ? '#0c1a2e' : '#1f1b02',
                      cursor: busy || locked ? 'not-allowed' : 'pointer',
                      padding: 0,
                    })}
                  >
                    {value}
                  </button>
                  <div style={{ color: '#4b5563', fontSize: '0.55rem', marginTop: 2 }}>
                    {measured.trueBases[i]}
                  </div>
                </div>
              );
            })}
          </div>

          {syndromes.length > 0 && (
            <div style={{ marginBottom: '0.7rem', fontSize: '0.76rem' }}>
              <div style={{ color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                Parity syndromes — satisfied{' '}
                <strong style={{ color: allSatisfied ? '#4ade80' : '#fbbf24' }}>{satisfied}</strong> / {syndromes.length}
              </div>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                {syndromes.map((p, i) => {
                  const ok = ((draft[i] ?? 0) ^ (draft[i + 1] ?? 0) ^ (draft[i + 2] ?? 0)) === p;
                  return (
                    <span key={i} title={`bits ${i}–${i + 2} must XOR to ${p}`} style={{
                      padding: '0.1rem 0.4rem', borderRadius: 3, fontSize: '0.7rem',
                      border: `1px solid ${ok ? '#4ade80' : '#4b5563'}`,
                      color: ok ? '#86efac' : '#9ca3af',
                    }}>
                      {i}-{i + 2}={p}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          <button
            className="primary"
            onClick={() => onSubmit({ key: draft })}
            disabled={busy}
            style={{ fontSize: '0.82rem', padding: '0.35rem 0.9rem' }}
          >
            {busy ? '…' : 'Submit the key'}
          </button>
          {allSatisfied && (
            <span style={{ color: '#4ade80', fontSize: '0.75rem', marginLeft: '0.6rem' }}>
              All parities check out.
            </span>
          )}
        </>
      )}
    </div>
  );
}
